import { Injectable } from '@angular/core';
import { combineLatest } from 'rxjs';
import { EnvironmentService } from 'src/app/system/utility/environment.service';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { MEAPIService } from '../../common/services/api/me-api.service';
import { UserService } from '../../common/services/user/user.service';
import { logger } from '../../common/services/logging/logging.service';
import { ExternalSettingsService } from 'src/app/common/services/external-settings/external-settings.service';
import { ExternalSettings } from 'src/app/common/services/external-settings/external-settings.interface';
import { InternalSettings, PermissionList } from 'src/app/common/services/internal-settings/internal-settings.interface';
import { hasOnePermission, InternalSettingsService } from 'src/app/common/services/internal-settings/internal-settings.service';
import { SocketMessageService } from 'src/app/common/services/message-handler/socket-message.service';

declare const CordovaWebsocketPlugin: any;

@Injectable({
    providedIn: 'root'
})
export class WebSocketService {

    readonly WEBSOCKET_API: string = MEAPIService.BASE_URL + '/websocketendpoint';

    private externalSettings!: ExternalSettings;
    private webSocketId: string | null = null;

    constructor(
        private srvUser: UserService,
        private srvIS: InternalSettingsService,
        private srvES: ExternalSettingsService,
        private srvSocketMessage: SocketMessageService
    ) { }

    public init() {

        if (!PlatformService.isMobile()) return;

        combineLatest([
            this.srvES.externalSettings$
        ]).subscribe(([externalSettings]) => {

            this.externalSettings = externalSettings

            this.initConnection();
        });
    }

    public connect(): Promise<string> {

        if (this.isConnected()) {
            logger.debug('WebSocket already connected:', this.webSocketId);
            return Promise.resolve(this.webSocketId!);
        }

        return new Promise((resolve, reject) => {
            // Only proceed if we're on a Mobile
            if (!PlatformService.isMobile()) {
                reject('WebSocket plugin is only available on mobile platforms');
                return;
            }

            const options: any = {
                url: this.getWebSocketURL(),
                timeout: 5000,
                pingInterval: 30000,
                headers: {
                    Cookie: "JSESSIONID=" + this.srvUser.getSession()
                },
                acceptAllCerts: true,
            };

            // Message handler
            const listener = (data: SocketMessage) => {
                logger.debug('WebSocket message received:', data);
                if (data.callbackMethod === 'onMessage') {
                    this.srvSocketMessage.deliverMessage(data);
                } else if (data.callbackMethod === 'onClose') {
                    logger.debug('WebSocket closed:', data);
                    this.webSocketId = null;
                } else if (data.callbackMethod === 'onFail') {
                    logger.error('WebSocket failed:', data);
                    this.webSocketId = null;
                }
            };

            // Success handler
            const success = (data: any) => {
                logger.debug('WebSocket connected successfully:', data);
                this.webSocketId = data.webSocketId;
                resolve(data.webSocketId);
            };

            // Error handler
            const error = (err: any) => {
                logger.error('WebSocket connection error:', err);
                this.webSocketId = null;
                reject(err);
            };

            // Connect using the Cordova plugin
            CordovaWebsocketPlugin.wsConnect(options, listener, success, error);
        });
    }

    public send(message: string): boolean {
        if (!this.webSocketId) {
            logger.debug('Connection lost, reconnecting...');
            this.connect();
            return false;
        }

        try {
            logger.debug('Sending message:', message);
            CordovaWebsocketPlugin.wsSend(this.webSocketId, message);
            return true;
        } catch (error) {
            logger.error('Error sending message:', error);
            return false;
        }
    }

    public close(): void {
        if (this.webSocketId) {
            try {
                CordovaWebsocketPlugin.wsClose(this.webSocketId, 1000, 'Normal closure');
                this.webSocketId = null;
            } catch (error) {
                logger.error('Error closing WebSocket:', error);
            }
        }
    }

    public isConnected(): boolean {
        return this.webSocketId !== null;
    }

    private getWebSocketURL(): string {
        if (PlatformService.isMobile()) {
            let url = EnvironmentService.getBaseUrl() + this.WEBSOCKET_API;
            return url.replace("https://", "wss://");
        }
        else {
            let port = window.location.port ? ':' + window.location.port : '';
            let url = window.location.protocol + '//' + window.location.hostname + port + this.WEBSOCKET_API;
            return url.replace("https://", "wss://").replace("http://", "ws://");
        }
    }

    private initConnection() {
        if (this.isMobileQueueEnable() || this.isHelpdeskEnable()) {
            this.srvUser.isLoggedIn$.subscribe((isLoggedIn) => {
                logger.info(`User Status change : ${isLoggedIn}`);

                // Release resources if not logged in
                if (!isLoggedIn) {
                    this.close();
                    return;
                }

                // If Queue is Enable, connect directly regardless of user permissions
                if (this.isMobileQueueEnable()) {
                    this.connect();
                    return;
                }

                // If Queue is Disable but Helpdesk enable, check permission after login
                if (this.isHelpdeskEnable()) {
                    this.srvIS.getCurrentSettings().subscribe((is: InternalSettings) => {
                        if (this.hasWebSocketServicePermission(is)) {
                            this.connect();
                            return;
                        }
                    });
                }
            });
        };
    }

    private isMobileQueueEnable() {
        return this.externalSettings.globalVariables.isModuleEnabledMobibranchQueue;
    }

    private isHelpdeskEnable() {
        return this.externalSettings.globalVariables.isModuleEnabledCustomerHelpDesk;
    }

    private hasWebSocketServicePermission(is: InternalSettings) {
        if (is == null) return false;
        return hasOnePermission([PermissionList.ACCESS_LIVE_CHAT], is.permissions);
    }

}

export interface SocketMessage {
    webSocketId: string;
    message: string;
    callbackMethod?: string;
}